// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Card }    from '@/components/ui/Card'
import { Input }   from '@/components/ui/Input'
import { Select }  from '@/components/ui/Select'
import { Toggle }  from '@/components/ui/Toggle'
import { Button }  from '@/components/ui/Button'
import { InfoButton } from '@/components/ui/InfoButton'
import { useWizardStore, useAuthStore } from '@/store'
import { useAutosave } from '@/hooks/useAutosave'
import { loadPets, savePets, loadPartner, loadChildren, EMPTY_PETS, EMPTY_PET, type PetsData, type PetEntry, type PartnerData, type ChildEntry } from '@/services/wizard'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import type { SaveStatus } from '@/hooks/useAutosave'

const SPECIES = ['Hond','Kat','Vogel','Konijn','Knaagdier','Cavia','Hamster','Vis','Reptiel','Schildpad','Paard','Pony','Anders'].map(v => ({ value: v, label: v }))
const PET_LOCATIONS = ['Thuis','Manege','Stal','Weiland','Pension','Opvangadres','Anders'].map(v => ({ value: v, label: v }))
const RELATIONS = ['Partner','Kind','Broer of zus','Vriend of vriendin','Buur','Familie','Kennis','Anders'].map(v => ({ value: v, label: v }))

export function NewStep03Pets({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { setAnswer } = useWizardStore()
  const { userId }    = useAuthStore()
  const [data,  setData]  = useState<PetsData>(EMPTY_PETS)
  const [ready, setReady] = useState(false)
  // Personen voor verzorger-keuzehulp
  const [ctxPartner, setCtxPartner] = useState<PartnerData | null>(null)
  const [ctxVP,      setCtxVP]      = useState<TrustedPersonConfig[]>([])
  const [ctxKids,    setCtxKids]    = useState<ChildEntry[]>([])

  useEffect(() => {
    if (!userId) return
    loadPets(userId).then(d => { if (d) setData(d); setReady(true) })
  }, [userId])

  // Laad partner, VP's en kinderen voor verzorger-keuzehulp
  useEffect(() => {
    if (!userId) return
    loadPartner(userId).then(p => { if (p) setCtxPartner(p) })
    loadTrustedPersons(userId).then(vps => {
      setCtxVP(vps.filter(v => v.confirmationStatus !== 'REVOKED'))
    })
    loadChildren(userId).then(d => {
      if (d?.hasChildren === 'yes') setCtxKids(d.children.filter(c => !!c.name))
    })
  }, [userId])

  const save = useCallback(async (d: PetsData) => { if (userId) await savePets(userId, d) }, [userId])
  const { status } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHasPets(v: string) {
    const pets = v === 'yes' && data.pets.length === 0 ? [{ ...EMPTY_PET }] : data.pets
    setData({ hasPets: v, pets: v === 'yes' ? pets : [] })
    setAnswer('hasPets', v === 'yes')
  }

  function upd(i: number, field: keyof PetEntry, value: string | boolean) {
    setData(p => { const pets = [...p.pets]; pets[i] = { ...pets[i], [field]: value }; return { ...p, pets } })
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-semibold text-ink mb-1">Huisdieren</h2>
        <p className="text-sm text-ink-3">Zodat ook uw huisdieren goede zorg krijgen als u er niet meer bent.</p>
        <InfoButton title="Huisdieren"
          content={<>
            <p>Leg hier vast wie er voor uw huisdier(en) kan zorgen en welke bijzonderheden de verzorger moet weten.</p>
            <p><strong>Tip:</strong> Vermeld ook het telefoonnummer van de dierenarts - die kent de medische voorgeschiedenis.</p>
            <p><strong>Bijzonderheden:</strong> denk aan medicatie, speciaal dieet, angsten of bijzonder gedrag.</p>
          </>} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-300 p-5">
        <p className="text-base font-medium text-ink mb-4">Heeft u huisdieren?</p>
        <div className="flex flex-col gap-2">
          {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }].map(opt => (
            <button key={opt.value} type="button" onClick={() => setHasPets(opt.value)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${data.hasPets === opt.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'}`}>
              <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${data.hasPets === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
              <span className="text-base font-semibold text-ink-2">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {data.hasPets === 'yes' && (
        <div className="flex flex-col gap-4 animate-slide-down">
          {data.pets.map((pet, i) => (
            <Card key={i}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-ink">Huisdier {i + 1}{pet.name ? ` - ${pet.name}` : ''}</p>
                {data.pets.length > 1 && (
                  <button onClick={() => setData(p => ({ ...p, pets: p.pets.filter((_, j) => j !== i) }))}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-3">
                {/* Basis */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <Input label="Naam" value={pet.name} onChange={e => upd(i, 'name', e.target.value)} placeholder="Max" />
                  <Select label="Diersoort" options={SPECIES} value={pet.species} onChange={e => upd(i, 'species', e.target.value)} placeholder="Kies..." />
                  <Input label="Ras (optioneel)" value={pet.breed ?? ''} onChange={e => upd(i, 'breed', e.target.value)} placeholder="Bijv: Labrador" />
                </div>
                <Input label="Leeftijd" value={pet.age} onChange={e => upd(i, 'age', e.target.value)} placeholder="Bijv: 5 jaar" />

                {/* Verblijfplaats - vooral relevant voor paard/pony of uitbesteed dier */}
                <div className="pt-1 border-t border-sand-200">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Verblijfplaats</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Select label="Waar verblijft het dier?" options={PET_LOCATIONS} value={pet.location ?? ''} onChange={e => upd(i, 'location', e.target.value)} placeholder="Kies..." />
                    {pet.location && pet.location !== 'Thuis' && (
                      <Input label="Naam / adres locatie" value={pet.locationDetails ?? ''} onChange={e => upd(i, 'locationDetails', e.target.value)} placeholder="Bijv: Manege De Vallei, Utrecht" />
                    )}
                  </div>
                </div>

                {/* Dierenarts */}
                <div className="pt-1 border-t border-sand-200">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Dierenarts</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input label="Naam dierenarts / praktijk" value={pet.vet} onChange={e => upd(i, 'vet', e.target.value)} placeholder="Dierenartspraktijk..." />
                    <Input label="Telefoonnummer" type="tel" value={pet.vetPhone} onChange={e => upd(i, 'vetPhone', e.target.value)} placeholder="010-..." />
                  </div>
                </div>

                {/* Verzorger - met keuzehulp */}
                {(() => {
                  const primaryVP  = ctxVP.find(v => v.role === 'PRIMARY')
                  const reserveVP  = ctxVP.find(v => v.role === 'RESERVE')
                  const hasPartner = ctxPartner?.hasPartner === 'yes' && !!ctxPartner.name
                  const hasKids    = ctxKids.length > 0
                  const hasPicker  = hasPartner || !!primaryVP || !!reserveVP || hasKids

                  // Picker-sleutel: relatie='Partner'|'Vertrouwenspersoon'|'Kind'
                  // en caretaker bevat de overeenkomende naam.
                  // Vrije invoer tonen als: geen picker beschikbaar, of caretakerRelation
                  // is niet één van de bekende picker-waarden.
                  const pickerRelations = ['Partner', 'Vertrouwenspersoon', 'Kind']
                  const isPickerChoice  = pickerRelations.includes(pet.caretakerRelation) && !!pet.caretaker
                  const showFree = !hasPicker || (!isPickerChoice && !!pet.caretakerRelation)

                  // Helper: zet verzorger in één keer
                  function pickCaretaker(name: string, relation: string) {
                    upd(i, 'caretaker',         name)
                    upd(i, 'caretakerRelation', relation)
                  }

                  return (
                    <div className="pt-1 border-t border-sand-200">
                      <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-1">
                        Wie kan voor dit dier zorgen na uw overlijden?
                      </p>
                      <p className="text-xs text-ink-4 mb-3">
                        Vul alleen in als u al iemand heeft aangewezen.
                      </p>

                      {/* Keuzeknoppen */}
                      {hasPicker && (
                        <div className="flex flex-col gap-2 mb-3">

                          {hasPartner && (() => {
                            const full = [ctxPartner!.name, ctxPartner!.lastName].filter(Boolean).join(' ')
                            const active = pet.caretakerRelation === 'Partner' && pet.caretaker === full
                            return (
                              <button type="button" onClick={() => pickCaretaker(full, 'Partner')}
                                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${active ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'}`}>
                                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                                <span className="text-sm text-ink">Partner - {full}</span>
                              </button>
                            )
                          })()}

                          {primaryVP && (() => {
                            const active = pet.caretakerRelation === 'Vertrouwenspersoon' && pet.caretaker === primaryVP.firstName
                            return (
                              <button type="button" onClick={() => pickCaretaker(primaryVP.firstName, 'Vertrouwenspersoon')}
                                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${active ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'}`}>
                                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                                <span className="text-sm text-ink">Eerste vertrouwenspersoon - {primaryVP.firstName}</span>
                              </button>
                            )
                          })()}

                          {reserveVP && (() => {
                            const active = pet.caretakerRelation === 'Vertrouwenspersoon' && pet.caretaker === reserveVP.firstName
                            return (
                              <button type="button" onClick={() => pickCaretaker(reserveVP.firstName, 'Vertrouwenspersoon')}
                                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${active ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'}`}>
                                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                                <span className="text-sm text-ink">Tweede vertrouwenspersoon - {reserveVP.firstName}</span>
                              </button>
                            )
                          })()}

                          {ctxKids.map((kid, ki) => {
                            const full   = [kid.name, kid.lastName].filter(Boolean).join(' ')
                            const active = pet.caretakerRelation === 'Kind' && pet.caretaker === full
                            return (
                              <button key={ki} type="button" onClick={() => pickCaretaker(full, 'Kind')}
                                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${active ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'}`}>
                                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                                <span className="text-sm text-ink">Kind - {full}</span>
                              </button>
                            )
                          })}

                          <button type="button"
                            onClick={() => { upd(i, 'caretaker', ''); upd(i, 'caretakerRelation', 'Anders') }}
                            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${showFree ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'}`}>
                            <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${showFree ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                            <span className="text-sm text-ink">Iemand anders invoeren</span>
                          </button>
                        </div>
                      )}

                      {/* Vrije invoer - als geen picker of 'Anders' gekozen */}
                      {(!hasPicker || showFree) && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input label="Naam" value={pet.caretaker}
                            onChange={e => upd(i, 'caretaker', e.target.value)}
                            placeholder="Voor- en achternaam" />
                          <Select label="Relatie" options={RELATIONS} value={pet.caretakerRelation}
                            onChange={e => upd(i, 'caretakerRelation', e.target.value)}
                            placeholder="Kies relatie..." />
                        </div>
                      )}

                      {/* Bevestiging bij picker-keuze */}
                      {isPickerChoice && hasPicker && !showFree && (
                        <div className="rounded-xl bg-sage-50 border border-sage-100 px-4 py-2.5 mt-1">
                          <p className="text-xs text-sage-700">
                            <strong>{pet.caretaker}</strong> ({pet.caretakerRelation}) is aangewezen als verzorger.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Verzorging */}
                <div className="pt-1 border-t border-sand-200">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Verzorging</p>
                  <div className="flex flex-col gap-2">
                    <Input label="Voeding" value={pet.food ?? ''} onChange={e => upd(i, 'food', e.target.value)} placeholder="Bijv: droogvoer 2x per dag..." />
                    <Input label="Medicatie" value={pet.medication ?? ''} onChange={e => upd(i, 'medication', e.target.value)} placeholder="Bijv: hartmedicatie, naam en dosering..." />
                    <Input label="Allergieën" value={pet.allergies ?? ''} onChange={e => upd(i, 'allergies', e.target.value)} placeholder="Bijv: glutenallergie..." />
                    <Input label="Karakter en gedrag" value={pet.behavior ?? ''} onChange={e => upd(i, 'behavior', e.target.value)} placeholder="Bijv: schuw voor vreemden, speels..." />
                    <div className="flex items-center justify-between rounded-xl bg-sand px-3 py-2.5">
                      <span className="text-sm text-ink-2">Bang voor vuurwerk of onweer?</span>
                      <Toggle label="" checked={pet.fireworkFear ?? false} onChange={v => upd(i, 'fireworkFear', v)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-2 mb-1.5">Overige bijzonderheden</label>
                      <textarea value={pet.specialNotes} onChange={e => upd(i, 'specialNotes', e.target.value)}
                        placeholder="Alles wat de verzorger moet weten..."
                        className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm text-ink resize-none focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent min-h-[70px]"
                        maxLength={400} />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={() => setData(p => ({ ...p, pets: [...p.pets, { ...EMPTY_PET }] }))} icon={<Plus className="h-4 w-4" />}>
            Nog een huisdier toevoegen
          </Button>
        </div>
      )}
      {data.pets.length > 0 && (
        <ClosingGuidance title="Ook uw huisdieren zijn meegenomen">
          Controleer regelmatig of de aangewezen verzorger en gemaakte afspraken nog actueel zijn.
        </ClosingGuidance>
      )}
    </div>
  )
}
